/**
 * Desktop centered Modal.
 *
 * Class names `web-modal-backdrop` / `web-modal-card` mirror the design
 * source (see design.md "Responsive shell" decision). Inline styles also
 * supplied because Vite consumers don't ship the design CSS file directly;
 * the class names act as design contract markers and as easy hooks for
 * scoped overrides later.
 *
 * Sharp edges:
 *   - Restores focus to the triggering element on close.
 *   - ESC closes via document listener (not just on the dialog) so global
 *     dismissal works even if focus has wandered.
 *   - role="dialog" + aria-modal so SR users hear the boundary.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { trapFocus } from './focusTrap';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Width of the card (defaults to 480). */
  width?: number;
  /** Optional aria-label override when `title` not present. */
  ariaLabel?: string;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  width = 480,
  ariaLabel,
}: ModalProps): JSX.Element | null {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Move focus into the card after mount so SR users land inside the dialog.
    const t = window.setTimeout(() => {
      cardRef.current?.focus();
    }, 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && cardRef.current) {
        trapFocus(e, cardRef.current);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      // Restore focus to opener.
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const labelledBy = title ? titleId : undefined;
  const ariaLabelAttr = title ? undefined : ariaLabel;

  return (
    <div
      className="web-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(36, 26, 16, 0.45)',
        zIndex: 100,
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div
        ref={cardRef}
        className="web-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={ariaLabelAttr}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-3)',
          width: '100%',
          maxWidth: width,
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        {title && (
          <div
            id={titleId}
            style={{
              padding: '18px 22px 14px',
              borderBottom: '1px solid var(--border-1)',
              font: '600 17px var(--font-cjk-display)',
              color: 'var(--fg-1)',
            }}
          >
            {title}
          </div>
        )}
        <div style={{ overflowY: 'auto', padding: '18px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
